<template>
  <div
    v-bind='$attrs'
    class='flex-wrap'
  >
    <label
      v-if='label'
      :for='name'
      class='block w-full text-sm font-normal'
      :class='[(disabled && editMode) ? "text-gray-400" : "text-gray-700"]'
      data-cy='label'
    >
      <QuestionMarkCircleIcon
        v-if='help'
        class='question-mark-circle-icon mb-1 mr-0.5 inline-block h-4 w-4 cursor-pointer'
        @click.stop='setHelpModalVisibility(true)'
      />
      <span
        :title='label'
        v-html='getLabel()'
      />
    </label>
    <template v-if='editMode && !disabled'>
      <div class='relative mt-1 w-full max-w-full'>
        <div
          v-if='withIcon'
          class='pointer-events-none absolute inset-y-0 left-3 flex items-center py-3'
        >
          <component
            :is='icon'
            class='h-6 w-6 shrink-0'
            aria-hidden='true'
          />
        </div>
        <div
          class='scroll-hidden flex w-full max-w-xl flex-row items-center gap-1 overflow-x-scroll rounded-md border px-2 text-sm shadow-sm focus-within:border-gray-500 focus-within:ring-1 focus-within:ring-gray-500'
          :class='[
            error ? "border-red-300 focus:ring-red-500 focus:border-red-500 caret-red-500 selection:bg-red-300 selection:text-red-900" : "border-gray-300 focus:ring-gray-500 focus:border-gray-500",
            disabled && "bg-gray-50 text-gray-400",
            withIcon && "pl-11",
            inputClasses,
          ]'
        >
          <div
            v-for='value in modelValue'
            :key='value'
            class='flex flex-row items-center gap-1.5 rounded-full bg-gray-300 px-3 py-0.5'
            :class='[ !value && "hidden" ]'
            data-cy='array-field-value'
          >
            <p class='w-max'>{{ value }}</p>
            <svg
              width='8'
              height='8'
              viewBox='0 0 8 8'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              class='cursor-pointer text-gray-500 hover:text-gray-400'
              data-cy='remove-array-field-value-button'
              @click='removeFieldValue(value, true)'
            >
              <path
                d='M7 1L1 7M1 1L7 7L1 1Z'
                stroke='currentColor'
                stroke-width='1.5'
                stroke-linecap='round'
                stroke-linejoin='round'
              />
            </svg>
          </div>
          <input
            :id='name'
            ref='arrayInput'
            :name='name'
            :type='type'
            :class='[inputClasses]'
            class='w-full min-w-fit border-0 bg-transparent px-0 text-sm focus:outline-0 focus:ring-transparent'
            :placeholder='modelValue?.length > 0 ? null : placeholder'
            :aria-describedby='ariaDescribedBy'
            :disabled='disabled'
            data-cy='input'
            @keydown.enter='addFieldValue($event.target.value)'
            @keydown.delete='removeFieldValue($event.target.value)'
            @keydown.space='addFieldValue($event.target.value)'
            @focus='$emit("has-focus", name)'
          >
        </div>
      </div>
      <InputError :error='error'/>
    </template>

    <template v-else>
      <div class='relative w-full max-w-full overflow-auto'>
        <div
          v-if='withIcon'
          class='pointer-events-none absolute inset-y-0 left-3 flex items-center py-3'
        >
          <component
            :is='icon'
            class='h-6 w-6 shrink-0'
            aria-hidden='true'
          />
        </div>
        <div
          class='scroll-hidden flex w-full max-w-xl flex-row items-center gap-1 overflow-x-scroll rounded-md text-sm'
          :class='[
            error ? "border-red-300 focus:ring-red-500 focus:border-red-500 caret-red-500 selection:bg-red-300 selection:text-red-900" : "border-gray-300 focus:ring-gray-500 focus:border-gray-500",
            disabled && "text-gray-400",
            withIcon && "pl-11",
            inputClasses,
          ]'
        >
          <div
            v-for='value in modelValue'
            :key='value'
            class='flex flex-row items-center gap-1.5 rounded-full bg-gray-300 px-3 py-0.5'
            :class='[ !value && "hidden" ]'
            data-cy='array-field-value'
          >
            <p class='w-max'>{{ value }}</p>
          </div>
        </div>
      </div>
      <InputError :error='error'/>
    </template>
  </div>
  <Modal
    data-cy='help-modal'
    :open='helpVisible'
    :title='label'
    @close-modal='setHelpModalVisibility(false)'
  >
    <template #content>
      <p v-html='help'/>
    </template>
  </Modal>
</template>

<script lang="ts">
  import { defineComponent, ref } from 'vue'
  import useInputFieldBase, { InputFieldProps, InputFieldComponents, InputFieldEmits } from '../../../compositions/useInputFieldBase'
  import useInputFieldHasHelp, { InputFieldHasHelpProps, InputFieldHasHelpComponents } from '../../../compositions/useInputFieldHasHelp'
  import { onClickOutside } from '@vueuse/core'

  export default defineComponent({
    name: 'ArrayField',
    components: {
      ...InputFieldComponents,
      ...InputFieldHasHelpComponents,
    },
    props: {
      ...InputFieldProps,
      ...InputFieldHasHelpProps,
      type: {
        type: String,
        default: 'text',
      },
      withIcon: {
        type: Boolean,
        default: false,
      },
      icon: {
        type: String,
        default: null,
      },
    },
    emits: [
      ...InputFieldEmits,
      'has-focus',
    ],
    setup(props) {
      const { helpVisible, setHelpModalVisibility } = useInputFieldHasHelp()
      const { updateValue, getLabel } = useInputFieldBase()

      const fieldValues = ref(props.modelValue ?? [] as Array<string> | null)
      const arrayInput = ref<HTMLInputElement>('')

      onClickOutside(arrayInput, () => addFieldValue(arrayInput.value.value))

      const addFieldValue = (value: string) => {
        fieldValues.value = fieldValues.value?.length ? fieldValues.value : []

        if (value && value.trim() !== '' && !fieldValues.value.includes(value.trim())) {
          fieldValues.value.push(value.trim())
          updateValue(fieldValues.value)
          arrayInput.value.value = ''
        }
      }

      const removeFieldValue = (value: string, deleteOnClick = false) => {
        if (Array.isArray(fieldValues.value) && fieldValues.value.length > 0) {
          if (! value && !deleteOnClick) fieldValues.value.pop()
          else if (value && deleteOnClick) fieldValues.value = fieldValues.value.filter((item: string) => item !== value)

          fieldValues.value = fieldValues.value[0] === undefined ? null : fieldValues.value
          updateValue(fieldValues.value)
        } else {
          fieldValues.value = null
          updateValue(null)
        }
      }

      return {
        updateValue, getLabel, helpVisible, setHelpModalVisibility, fieldValues, addFieldValue, removeFieldValue, arrayInput,
      }
    },
  })
</script>

<style scoped>
.scroll-hidden::-webkit-scrollbar {
  display: none;
}

.scroll-hidden {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
</style>
